/**
 * Admin Service
 * Handles admin API operations (user management, audit logs, stats)
 */

let ADMIN_API_URL = ""

async function loadAdminApiUrl(): Promise<string> {
  if (ADMIN_API_URL) return ADMIN_API_URL
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  const baseUrl = config.feedbackApiUrl
  if (!baseUrl) throw new Error("API URL not found")
  ADMIN_API_URL = `${baseUrl.replace(/\/?$/, "/")}`
  return ADMIN_API_URL
}

async function fetchWithAuth(url: string, idToken: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`)
  }
  return response.json()
}

// --- User Management ---
export interface User {
  userId: string
  email: string
  role: string
  enabled: boolean
  status: string
  /** 自由編成（run_subagents）の開放状態（membership 由来・キー無し=false） */
  dynamicAgentsEnabled?: boolean
}

export async function listUsers(idToken: string): Promise<User[]> {
  const url = await loadAdminApiUrl()
  const data = await fetchWithAuth(`${url}admin/users`, idToken)
  return data.users || []
}

export async function inviteUser(idToken: string, email: string, role: string): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/users/invite`, idToken, {
    method: "POST",
    body: JSON.stringify({ email, role }),
  })
}

/** 招待メールの再送（owner のみ・仮パスワード未変更のユーザーが対象・2026-07-02 P3） */
export async function resendInvite(idToken: string, email: string): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/users/resend-invite`, idToken, {
    method: "POST",
    body: JSON.stringify({ email }),
  })
}

export async function updateUserRole(idToken: string, userId: string, role: string): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/users/${userId}/role`, idToken, {
    method: "PUT",
    body: JSON.stringify({ role }),
  })
}

export async function updateUserStatus(idToken: string, userId: string, enabled: boolean): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/users/${userId}/status`, idToken, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  })
}

/** 自由編成（run_subagents）の開放切替（owner のみ・反映は次に登録するタスクから） */
export async function setUserDynamicAgents(
  idToken: string,
  userId: string,
  enabled: boolean
): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/users/${userId}/dynamic-agents`, idToken, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  })
}

/**
 * Delete a user from the system.
 * @param idToken - The caller's Cognito ID token for authorization.
 * @param userId - The Cognito sub (UUID) of the user to delete.
 */
export async function deleteUser(idToken: string, userId: string): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/users/${encodeURIComponent(userId)}`, idToken, {
    method: "DELETE",
  })
}

// --- Usage Limits (Owner-only) ---

export type RoleName = "owner" | "admin" | "member" | "viewer"

/** GET /admin/usage-limits のレスポンス（既定＋テナント上書き＋ユーザー上書き）。 */
export interface UsageLimits {
  /** ロール別の日次クレジット既定（env 注入のマージ後）。 */
  defaults: Record<RoleName, number>
  /** ロール別の上書き（設定済みのロールのみ）。 */
  roleOverrides: Partial<Record<RoleName, number>>
  /** テナント月次。base は env 既定（null=無制限）、effective=base+topup（base が null なら null）。 */
  monthly: { base: number | null; topup: number; effective: number | null }
  /** 日次上限を上書き済みのユーザー一覧。 */
  userOverrides: Array<{ userId: string; email: string; role: string; credits: number }>
  /** 最終更新（ISO-8601・未設定なら空文字）。 */
  updatedAt: string
  /** 最終更新者の Cognito sub。 */
  updatedBy: string
}

/** Owner 専用: 利用上限の現状を取得する。 */
export async function getUsageLimits(idToken: string): Promise<UsageLimits> {
  const url = await loadAdminApiUrl()
  return fetchWithAuth(`${url}admin/usage-limits`, idToken)
}

/**
 * Owner 専用: ロール別の日次クレジット上限を設定/解除する。
 * @param credits 新しい上限（>=0）。null で上書きを解除し env 既定へ戻す。
 */
export async function setRoleCredits(
  idToken: string,
  role: RoleName,
  credits: number | null
): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/usage-limits/role/${role}`, idToken, {
    method: "PUT",
    body: JSON.stringify({ credits }),
  })
}

/**
 * Owner 専用: ユーザー別の日次クレジット上限を設定/解除する。
 * @param credits 新しい上限（>=0）。null で上書きを解除しロール既定へ戻す。
 */
export async function setUserCredits(
  idToken: string,
  userId: string,
  credits: number | null
): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/usage-limits/user/${encodeURIComponent(userId)}`, idToken, {
    method: "PUT",
    body: JSON.stringify({ credits }),
  })
}

/** Owner 専用: テナント月次クレジットの topup（積み増し・絶対値）を設定する。 */
export async function setMonthlyTopup(idToken: string, credits: number): Promise<void> {
  const url = await loadAdminApiUrl()
  await fetchWithAuth(`${url}admin/usage-limits/monthly-topup`, idToken, {
    method: "PUT",
    body: JSON.stringify({ credits }),
  })
}

// --- Audit / Stats ---
export interface AuditStats {
  activeUsers: number
  totalRequests: number
  guardrailBlocks: number
  errorCount: number
  recentAlerts: AuditEvent[]
}

export interface AuditEvent {
  sessionId: string
  "timestamp#eventId": string
  timestamp: number
  userId: string
  eventType: string
  toolName?: string
  inputSummary?: string
  outputSummary?: string
  latencyMs?: number
}

export async function getAuditStats(idToken: string): Promise<AuditStats> {
  const url = await loadAdminApiUrl()
  return fetchWithAuth(`${url}admin/audit/stats`, idToken)
}

export async function searchAuditLogs(
  idToken: string,
  params: { userId?: string; from?: number; to?: number; eventType?: string; limit?: number; cursor?: string }
): Promise<{ events: AuditEvent[]; count: number; nextCursor: string | null }> {
  const url = await loadAdminApiUrl()
  const query = new URLSearchParams()
  if (params.userId) query.set("userId", params.userId)
  if (params.from) query.set("from", String(params.from))
  if (params.to) query.set("to", String(params.to))
  if (params.eventType) query.set("eventType", params.eventType)
  if (params.limit) query.set("limit", String(params.limit))
  if (params.cursor) query.set("cursor", params.cursor)
  return fetchWithAuth(`${url}admin/audit?${query}`, idToken)
}

export async function exportAuditLogs(
  idToken: string,
  params: { format: "csv" | "json"; userId?: string; from?: number; to?: number }
): Promise<{ data: string | AuditEvent[]; contentType: string }> {
  const url = await loadAdminApiUrl()
  const query = new URLSearchParams()
  query.set("format", params.format)
  if (params.userId) query.set("userId", params.userId)
  if (params.from) query.set("from", String(params.from))
  if (params.to) query.set("to", String(params.to))
  return fetchWithAuth(`${url}admin/audit/export?${query}`, idToken)
}
